<template>
  <div class="content-wrapper p-3 p-md-4">
    <div class="d-flex justify-content-between align-items-center mb-3">
      <div>
        <h4 class="mb-1">สมาชิกในสาขา</h4>
        <small class="text-muted">
          {{ branchInfo && branchInfo.name }}
          <span v-if="branchInfo && branchInfo.clinic">
            · {{ branchInfo.clinic.nameTh || branchInfo.clinic.name }}
          </span>
        </small>
      </div>
      <div>
        <b-button
          variant="primary"
          :disabled="assignableBranchRoles.length === 0"
          @click="openAssignDialog(null)"
        >
          <i class="fas fa-user-plus mr-1"></i>
          มอบบทบาทให้ผู้ใช้
        </b-button>
      </div>
    </div>

    <b-card no-body>
      <div class="p-3 border-bottom d-flex align-items-center">
        <b-form-input
          v-model="searchText"
          placeholder="ค้นหาชื่อ / อีเมล..."
          size="sm"
          class="mr-2"
          style="max-width: 280px"
        />
        <b-button size="sm" variant="outline-secondary" @click="refresh">
          <b-spinner v-if="isLoading" small class="mr-1" />
          <i v-else class="fas fa-sync-alt mr-1"></i>
          รีเฟรช
        </b-button>
        <small v-if="!isLoading" class="ml-auto text-muted">
          {{ filteredMembers.length }} / {{ members.length }} คน
        </small>
      </div>

      <b-table
        :items="filteredMembers"
        :fields="fields"
        :busy="isLoading"
        hover
        responsive
        empty-text="ไม่มีสมาชิกในสาขานี้"
        show-empty
        striped
      >
        <template #table-busy>
          <div class="text-center py-4">
            <b-spinner class="align-middle" />
            <span class="ml-2">กำลังโหลด...</span>
          </div>
        </template>

        <template #cell(name)="row">
          <strong>{{ row.item.firstNameTh }} {{ row.item.lastNameTh }}</strong>
        </template>

        <template #cell(email)="row">
          <span v-if="row.item.email" class="member-email">{{ row.item.email }}</span>
          <span v-else class="text-muted small">—</span>
        </template>

        <template #cell(roles)="row">
          <div class="d-flex flex-wrap" style="gap: 0.4rem">
            <b-badge
              v-for="rk in row.item.roleKeys"
              :key="rk"
              variant="info"
              class="d-flex align-items-center"
              style="gap: 0.4rem; padding-right: 0.4rem"
            >
              {{ roleLabel(rk) }}
              <b-button
                v-if="rk === 'MANAGER' && isLastManager && canTransferManager"
                variant="link"
                size="sm"
                class="p-0 text-white"
                style="line-height: 1"
                title="โอนย้ายผู้จัดการ"
                @click="openSwapDialog(row.item)"
              >
                <i class="fas fa-exchange-alt"></i>
              </b-button>
              <b-button
                v-else-if="canRemove(rk) && !(rk === 'MANAGER' && isLastManager)"
                variant="link"
                size="sm"
                class="p-0 text-white"
                style="line-height: 1"
                :title="'ถอดบทบาท ' + roleLabel(rk)"
                @click="openRemoveDialog(row.item, rk)"
              >
                <i class="fas fa-times-circle"></i>
              </b-button>
            </b-badge>
            <span v-if="row.item.roleKeys.length === 0" class="text-muted small">
              ไม่มีบทบาท
            </span>
          </div>
        </template>

        <template #cell(actions)="row">
          <b-button
            size="sm"
            variant="outline-primary"
            :disabled="assignableBranchRoles.length === 0"
            @click="openAssignDialog(row.item)"
          >
            <i class="fas fa-plus mr-1"></i>
            เพิ่มบทบาท
          </b-button>
        </template>
      </b-table>
    </b-card>

    <AssignRoleDialog ref="AssignRoleDialog" @assigned="refresh" />
    <RemoveRoleDialog ref="RemoveRoleDialog" @removed="refresh" />
    <SwapManagerDialog ref="SwapManagerDialog" @swapped="refresh" />
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import axios from "axios";
import AssignRoleDialog from "@/components/user-mgmt/AssignRoleDialog.vue";
import RemoveRoleDialog from "@/components/user-mgmt/RemoveRoleDialog.vue";
import SwapManagerDialog from "@/components/user-mgmt/SwapManagerDialog.vue";
import { ROLE_LABELS_TH } from "@/constants/roles";

export default {
  name: "BranchMembers",
  components: { AssignRoleDialog, RemoveRoleDialog, SwapManagerDialog },
  data() {
    return {
      members: [],
      isLoading: false,
      searchText: "",
      fields: [
        { key: "name", label: "ชื่อ", sortable: true },
        { key: "email", label: "อีเมล", sortable: true },
        { key: "roles", label: "บทบาท" },
        { key: "actions", label: "", thStyle: { width: "150px" } },
      ],
    };
  },
  computed: {
    ...mapGetters({
      branchInfo: "moduleUser/getBranchInfo",
      assignableRolesGetter: "moduleUser/assignableRoles",
      can: "moduleUser/can",
    }),
    clinicUrl() {
      return this.$route.params.clinicUrl;
    },
    branchUrl() {
      return this.$route.params.branchUrl;
    },
    assignableBranchRoles() {
      return this.assignableRolesGetter("branch");
    },
    managerCount() {
      return this.members.filter(m => (m.roleKeys || []).includes("MANAGER"))
        .length;
    },
    isLastManager() {
      // When exactly one MANAGER remains, plain removal is blocked by the BE
      // last-MANAGER guard (#7) — offer a transfer instead.
      return this.managerCount === 1;
    },
    canTransferManager() {
      const clinicId = this.branchInfo?.clinic?.id;
      const branchId = this.branchInfo?.id;
      return (
        this.can("user.assign.MANAGER", { clinicId, branchId }) &&
        this.can("user.remove.branch", { clinicId, branchId })
      );
    },
    filteredMembers() {
      const q = this.searchText.trim().toLowerCase();
      if (!q) return this.members;
      return this.members.filter(m => {
        const name = `${m.firstNameTh || ""} ${m.lastNameTh || ""}`.toLowerCase();
        const email = String(m.email || "").toLowerCase();
        return name.includes(q) || email.includes(q);
      });
    },
  },
  async created() {
    await this.ensurePermissionContext();
    await this.refresh();
  },
  methods: {
    roleLabel(rk) {
      return ROLE_LABELS_TH[rk] || rk;
    },
    canRemove() {
      const clinicId = this.branchInfo?.clinic?.id;
      const branchId = this.branchInfo?.id;
      return this.can("user.remove.branch", { clinicId, branchId });
    },
    async ensurePermissionContext() {
      // ensurePermissionContextFor handles its own cache check by clinicURL —
      // re-loads only when the active context belongs to a different clinic.
      try {
        await this.$store.dispatch("moduleUser/ensurePermissionContextFor", {
          clinicURL: this.clinicUrl,
        });
      } catch (err) {
        // Will fall back to BE 403 + refresh prompt.
        // eslint-disable-next-line no-console
        console.warn("loadPermissionContext failed", err);
      }
    },
    async refresh() {
      this.isLoading = true;
      try {
        const res = await axios.get(
          `/api/userMgmt/${this.clinicUrl}/branch/${this.branchUrl}/members`
        );
        this.members = Array.isArray(res.data) ? res.data : [];
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Failed to load branch members", err);
        this.members = [];
      } finally {
        this.isLoading = false;
      }
    },
    openAssignDialog(targetUser) {
      if (!targetUser) {
        // No user yet — pick from existing branch members. The back-end already
        // resolved the universe of users with branch access; this dialog
        // assumes a target uid. For "Assign to new user", we need a user
        // picker — but the simpler path is to direct admins to the clinic-wide
        // page. For now: open without target if branch is empty, otherwise
        // require row-level click.
        // Fallback: prompt the user to pick a row.
        this.$bvModal.msgBoxOk(
          "เลือกผู้ใช้จากตารางก่อน หรือใช้หน้า 'ผู้ใช้ในคลินิก' เพื่อมอบบทบาทให้ผู้ใช้ใหม่",
          { title: "เลือกผู้ใช้", centered: true, okTitle: "ตกลง" }
        );
        return;
      }
      this.$refs.AssignRoleDialog.show({
        scope: "branch",
        targetUser,
        clinicUrl: this.clinicUrl,
        branchUrl: this.branchUrl,
        branchId: this.branchInfo?.id,
      });
    },
    openRemoveDialog(targetUser, roleKey) {
      this.$refs.RemoveRoleDialog.show({
        scope: "branch",
        targetUser,
        roleKey,
        clinicUrl: this.clinicUrl,
        branchUrl: this.branchUrl,
        branchLabel: this.branchInfo?.name,
      });
    },
    openSwapDialog(currentManager) {
      this.$refs.SwapManagerDialog.show({
        clinicUrl: this.clinicUrl,
        branchUrl: this.branchUrl,
        branchLabel: this.branchInfo?.name,
        currentManager,
        candidates: this.members,
      });
    },
  },
};
</script>

<style scoped>
.member-email {
  color: #475569;
  font-size: 0.85rem;
  word-break: break-all;
}
</style>
